// src/components/InactivityWatcher.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "services/supabaseClient";
import { useProfile } from "context/ProfileContext";
import { useAppAccess } from "context/AppAccessContext";
import ModalBackdrop from "components/ModalBackdrop";
import {
  GRACE_PERIOD_MS,
  INACTIVITY_LIMIT_MS,
  MAX_IDLE_MS,
  clearActivityState,
  isIdleSessionExpired,
  readLastActivity,
  writeLastActivity,
} from "lib/sessionActivity";

const INACTIVITY_LOGOUT_KEY = "compass.inactivityLogout";
const MANUAL_LOGOUT_KEY = "compass.manualLogout";
const DEBT_STUDIO_SESSION_KEY = "debtstudio.session";
const DEBT_STUDIO_BACKUP_KEY = "debtstudio.lastSessionBackup";

function clearAppSession() {
  try {
    sessionStorage.removeItem(DEBT_STUDIO_SESSION_KEY);
  } catch {
    // ignore storage failures
  }
}

function backupDebtStudioSession() {
  try {
    const dsSession = sessionStorage.getItem(DEBT_STUDIO_SESSION_KEY);
    if (dsSession) {
      localStorage.setItem(DEBT_STUDIO_BACKUP_KEY, dsSession);
    }
  } catch (err) {
    console.warn("[InactivityWatcher] Could not back up DebtStudio session:", err);
  }
}

export default function InactivityWatcher() {
  const { refreshProfile } = useProfile();
  const { refreshApps } = useAppAccess();

  const [warning, setWarning] = useState(false);
  const [remaining, setRemaining] = useState(GRACE_PERIOD_MS / 1000);

  const inactivityTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const tickIntervalRef = useRef(null);
  const deadlineRef = useRef(0);
  const lastActivityRef = useRef(readLastActivity(Date.now()));
  const warningRef = useRef(false);
  const signingOutRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }

    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }

    if (tickIntervalRef.current) {
      clearInterval(tickIntervalRef.current);
      tickIntervalRef.current = null;
    }
  }, []);

  const updateRemaining = useCallback(() => {
    const msLeft = Math.max(0, deadlineRef.current - Date.now());
    const secondsLeft = Math.ceil(msLeft / 1000);
    setRemaining(secondsLeft);
    return secondsLeft;
  }, []);

  const expireSession = useCallback(async () => {
    if (signingOutRef.current) return;

    // A manual logout owns the routing decision. A grace-period timer may
    // already be queued when the user clicks Log Out, so do not let that
    // timer reclassify the logout as inactivity and send the next visit to
    // /session-expired.
    try {
      if (localStorage.getItem(MANUAL_LOGOUT_KEY) === "1") {
        signingOutRef.current = true;
        clearTimers();
        return;
      }
    } catch {
      // Continue with inactivity logout if storage is unavailable.
    }

    signingOutRef.current = true;

    clearTimers();
    backupDebtStudioSession();
    clearAppSession();

    try {
      localStorage.setItem(INACTIVITY_LOGOUT_KEY, "1");
      localStorage.removeItem(MANUAL_LOGOUT_KEY);
      clearActivityState();
    } catch {
      // ignore storage failures
    }

    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn("[InactivityWatcher] signOut failed:", err);
    } finally {
      window.location.replace("/session-expired");
    }
  }, [clearTimers]);

  useEffect(() => {
    try {
      if (localStorage.getItem(INACTIVITY_LOGOUT_KEY) === "1") {
        void expireSession();
        return;
      }

      if (isIdleSessionExpired()) {
        void expireSession();
      }
    } catch {
      // ignore storage failures
    }
  }, [expireSession]);

  const startGracePeriod = useCallback((deadlineAt = Date.now() + GRACE_PERIOD_MS) => {
    clearTimers();

    warningRef.current = true;
    deadlineRef.current = deadlineAt;

    setWarning(true);
    updateRemaining();

    tickIntervalRef.current = setInterval(() => {
      const secondsLeft = updateRemaining();
      if (secondsLeft <= 0 && tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
    }, 1000);

    const msLeft = Math.max(0, deadlineAt - Date.now());
    logoutTimerRef.current = setTimeout(() => {
      void expireSession();
    }, msLeft);
  }, [clearTimers, expireSession, updateRemaining]);

  const startInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    const msUntilWarning = Math.max(
      0,
      lastActivityRef.current + INACTIVITY_LIMIT_MS - Date.now()
    );

    inactivityTimerRef.current = setTimeout(() => {
      startGracePeriod(lastActivityRef.current + MAX_IDLE_MS);
    }, msUntilWarning);
  }, [startGracePeriod]);

  const resetSessionTimer = useCallback(() => {
    if (signingOutRef.current || warningRef.current) return;
    clearTimers();
    setWarning(false);
    setRemaining(GRACE_PERIOD_MS / 1000);
    deadlineRef.current = 0;
    lastActivityRef.current = Date.now();
    writeLastActivity(lastActivityRef.current);
    startInactivityTimer();
  }, [clearTimers, startInactivityTimer]);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_OUT") return;

      clearTimers();
      setWarning(false);
      warningRef.current = false;
      deadlineRef.current = 0;
    });

    return () => sub?.subscription?.unsubscribe();
  }, [clearTimers]);

  useEffect(() => {
    const events = [
      "mousemove",
      "keydown",
      "mousedown",
      "touchstart",
      "scroll",
      "wheel",
    ];

    const onActivity = () => {
      resetSessionTimer();
    };

    events.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });

    resetSessionTimer();

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
      clearTimers();
    };
  }, [clearTimers, resetSessionTimer]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden || signingOutRef.current) return;

      if (warningRef.current) {
        const secondsLeft = updateRemaining();
        if (secondsLeft <= 0) {
          void expireSession();
        }
        return;
      }

      const storedLastActivity = readLastActivity(lastActivityRef.current);
      lastActivityRef.current = storedLastActivity;

      const idleFor = Date.now() - storedLastActivity;
      if (idleFor >= MAX_IDLE_MS) {
        void expireSession();
        return;
      }

      if (idleFor >= INACTIVITY_LIMIT_MS) {
        startGracePeriod(storedLastActivity + MAX_IDLE_MS);
        return;
      }

      resetSessionTimer();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [expireSession, resetSessionTimer, startGracePeriod, updateRemaining]);

  const handleRefresh = async () => {
    clearTimers();
    warningRef.current = false;
    deadlineRef.current = 0;
    setWarning(false);
    setRemaining(GRACE_PERIOD_MS / 1000);
    lastActivityRef.current = Date.now();
    writeLastActivity(lastActivityRef.current);

    // Refresh account data in place so the active route and its workspace stay mounted.
    await refreshProfile(false);
    await refreshApps(true);

    startInactivityTimer();
  };

  if (!warning) return null;

  const mins = Math.floor(remaining / 60);
  const secs = String(Math.floor(remaining % 60)).padStart(2, "0");

  return (
    <ModalBackdrop zIndex="z-[1100]">
      <div
        className="bg-app text-app p-6 rounded-xl shadow-lg max-w-sm w-full text-center border border-app"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="inactivity-warning-title"
      >
        <h2 id="inactivity-warning-title" className="text-lg font-semibold mb-2 text-app">
          Session Inactive
        </h2>
        <p className="text-sm text-muted mb-4">
          This session has been inactive for 30 minutes,
          <br />
          you&apos;ll be logged off in{" "}
          <span className="font-semibold text-brand">
            {mins}:{secs}
          </span>
          .
        </p>
        <div className="flex justify-center">
          <button className="btn btn-primary" onClick={handleRefresh}>
            Continue Session
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
